import React, { useEffect, useState } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  useLocation,
  useHistory,
  useParams,
  withRouter
} from "react-router-dom";
import PropTypes from "prop-types";
import { Navbar } from "reactstrap";
import _ from "lodash";
import eventType from "../../../prop-types/eventType";
import NavigationLinks from "../components/navigation-links";
import clientPropType from "../../../prop-types/client";
import { ClientType, EVENTS, SCHEDULED } from "../../../constants";
import { eventSelected } from "../../../state/events/actions";
import { logout } from "../../../state/user/actions";
import { updateFilters } from "../../../state/filters/actions";
import { getItemFromLS } from "../../../utils/util";
import "../styles/navigation.scss";

const propTypes = {
  tabName: PropTypes.string.isRequired,
  types: PropTypes.arrayOf(eventType).isRequired,
  // Map state to props
  client: clientPropType,
  user: PropTypes.shape({
    loggedIn: PropTypes.bool
  }).isRequired,
  filters: PropTypes.shape({
    clientId: PropTypes.number,
    conferenceId: PropTypes.number,
    sport: PropTypes.number,
    eventType: PropTypes.number.isRequired,
    category: PropTypes.number,
    tabName: PropTypes.string
  }).isRequired,
  // Map dispatch to props
  handleFiltersUpdate: PropTypes.func.isRequired,
  handleLogout: PropTypes.func.isRequired
};

const defaultProps = {
  client: null
};

function Navigation(props) {
  const {
    tabName,
    types,
    client,
    filters,
    user,
    handleFiltersUpdate,
    handleLogout
  } = props;

  const location = useLocation();
  const history = useHistory();
  const params = useParams();

  const [isFiltersMenuOpen, setIsFiltersMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);

  const toggleFiltersNav = () => {
    setIsFiltersMenuOpen(!isFiltersMenuOpen);
    if (isAccountMenuOpen) {
      // Close it
      setIsAccountMenuOpen(false);
    }
  };

  const toggleAccountNav = () => {
    setIsAccountMenuOpen(!isAccountMenuOpen);
    if (isFiltersMenuOpen) {
      // Close it
      setIsFiltersMenuOpen(false);
    }
  };

  const toggleSideNavs = () => {
    if (isFiltersMenuOpen) {
      setIsFiltersMenuOpen(false);
    }

    if (isAccountMenuOpen) {
      setIsAccountMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleOverlayClick = (e) => {
      if (e.target.className === "navigation-overlay") {
        setIsFiltersMenuOpen(false);
        setIsAccountMenuOpen(false);
      }
    };
    window.addEventListener("click", handleOverlayClick);

    return () => {
      window.removeEventListener("click", handleOverlayClick);
    };
  }, []);

  const logUserOut = () => {
    handleLogout();

    if (tabName !== SCHEDULED) {
      toggleSideNavs();
      handleFiltersUpdate({ ...filters, tabName: SCHEDULED });
      history.push(
        `/${params.shortName}/${EVENTS}/${SCHEDULED}${location.search}`
      );
    }
  };

  const onConferenceFilter = (conferenceId) => () => {
    filters.conferenceId = conferenceId;
    // If the user has a conference and a client selected
    if (!_.isNull(conferenceId) && filters.clientId !== client?.id) {
      // But the school is not part of the conference
      if (
        client?.grandchildren.find((c) => c.id === filters.clientId)
          .parentClientId !== conferenceId
      ) {
        // We clear the school filter
        filters.clientId = client?.id;
      }
    }
    handleFiltersUpdate(filters);
  };

  const onSchoolFilter = (schoolId) => () => {
    const isOrg = client?.clientType.toLowerCase() === ClientType.ORGANIZATION;
    const school = isOrg
      ? client?.grandchildren.find((c) => c.id === schoolId)
      : client?.siblings.find((c) => c.id === schoolId);
    filters.clientId = schoolId;
    if (
      school &&
      filters.conferenceId &&
      school.parentClientId !== filters.conferenceId
    ) {
      filters.conferenceId = null;
    }
    handleFiltersUpdate(filters);
  };

  const selectedSportCallback = (value) => () => {
    const searchParams = new URLSearchParams(location.search.toLowerCase());
    if (+value) {
      filters.sport = value;
      searchParams.set("sport", value);
    } else {
      filters.sport = 0;
      searchParams.delete("sport", value);
    }
    handleFiltersUpdate(filters);
    history.push(`${location.pathname}?${searchParams.toString()}`);
  };

  const selectedCategoryCallback = (value) => () => {
    const searchParams = new URLSearchParams(location.search.toLowerCase());
    if (+value) {
      filters.category = value;
      searchParams.set("category", value);
    } else {
      filters.category = 0;
      searchParams.delete("category", value);
    }
    handleFiltersUpdate(filters);
    history.push(`${location.pathname}?${searchParams.toString()}`);
  };

  const onEventTypeFilter = (value) => () => {
    filters.eventType = value;
    handleFiltersUpdate(filters);
  };

  const typeOptions = [
    { value: 0, text: "All Sports" },
    ..._.orderBy(
      types
        .filter((t) => !!t.sport)
        .map((type) => ({
          value: type.id,
          text: type.name
        })),
      "text",
      "asc"
    )
  ];

  const isLoggedIn = getItemFromLS("tokenId");

  return (
    <>
      {client && (
        <header role="navigation">
          <Navbar className="navbar-mobile" fixed="top">
            <NavigationLinks
              client={client}
              user={user}
              filters={filters}
              activeTab={tabName}
              typeOptions={typeOptions}
              isFiltersMenuOpen={isFiltersMenuOpen}
              isAccountMenuOpen={isAccountMenuOpen}
              isLoggedIn={!!isLoggedIn}
              logout={logUserOut}
              toggleFiltersNav={toggleFiltersNav}
              toggleAccountNav={toggleAccountNav}
              toggleSideNavs={toggleSideNavs}
              onSchoolFilter={onSchoolFilter}
              onConferenceFilter={onConferenceFilter}
              onEventTypeFilter={onEventTypeFilter}
              selectedSportCallback={selectedSportCallback}
              selectedCategoryCallback={selectedCategoryCallback}
            />
          </Navbar>
        </header>
      )}
    </>
  );
}

Navigation.propTypes = propTypes;
Navigation.defaultProps = defaultProps;

const mapStateToProps = (state) => ({
  client: state.client.client,
  filters: state.filters.filters,
  user: state.user.user
});

const mapDispatchToProps = {
  eventSelectedAction: eventSelected,
  handleFiltersUpdate: updateFilters,
  handleLogout: logout
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(Navigation);
